import { Controller } from '@hotwired/stimulus';

/**
 * Opens/closes a native <dialog> by DOM *scope* instead of a globally-unique
 * id.
 *
 * Why: the native Invoker Commands API (`commandfor`/`command`) resolves its
 * target through `document.getElementById`, so every dialog must carry a
 * unique id. In layouts that render the same markup more than once
 * (mobile/desktop sidebars, print copies) that forces either a fixed id that
 * collides across copies, or a random id that has to be threaded from the
 * dialog to its trigger — the "random ids → random failures" problem Jan
 * flagged. Scoping the lookup removes the need for ids entirely and restores
 * the scoping the old `InlineModal` gave us.
 *
 * Two wiring styles are supported:
 *
 * 1. Container-scoped target (preferred — fully self-contained, no id, no
 *    selector string):
 *
 *      <div data-controller="modal-trigger">
 *        <dialog data-modal-trigger-target="dialog">…</dialog>
 *        <button data-action="modal-trigger#open">Open</button>
 *        <button data-action="modal-trigger#close">Close</button>
 *      </div>
 *
 * 2. Selector within an explicit scope (when the trigger and the dialog can't
 *    share a single wrapper, e.g. the trigger lives in a toolbar and the
 *    dialog in an aside):
 *
 *      <button data-controller="modal-trigger"
 *        data-modal-trigger-scope-value="aside"
 *        data-modal-trigger-selector-value="dialog[data-file-id='1234']"
 *        data-action="modal-trigger#open">
 *
 *    `scope` is a selector for the nearest ancestor to search within (defaults
 *    to `document`); `selector` finds the dialog inside that scope. The
 *    selector only has to be unique *within the scope*, not globally.
 */
export default class extends Controller {
  static targets = ['dialog'];
  static values = {
    selector: String,
    scope: String,
  };

  open() {
    this.dialog?.showModal();
  }

  close() {
    this.dialog?.close();
  }

  get dialog() {
    if (this.hasDialogTarget) return this.dialogTarget;
    if (!this.hasSelectorValue) return null;

    return this.scopeRoot?.querySelector(this.selectorValue) ?? null;
  }

  // Fail closed: when an explicit scope is set but no matching ancestor
  // exists, return null instead of falling back to `document`, so a stale or
  // mistyped scope can't resolve to some other matching dialog on the page.
  get scopeRoot() {
    if (!this.hasScopeValue) return document;
    return this.element.closest(this.scopeValue);
  }
}
